import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useLoadingToast } from "../hooks/toast/toasts";
import CompleteLogo from "../media/logo/CompleteLogoBlue.png";
import { resetPassword, validateResetToken } from "../services/api/accounts/accountsApi";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [success, setSuccess] = useState(false);
  
  // Estados de validação do token
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");

  // Calcular força da senha
  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: "", color: "#e5e7eb" };
    
    let score = 0;
    if (password.length >= 6) score += 25;
    if (password.length >= 8) score += 15;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 20;
    if (/\d/.test(password)) score += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 20;
    
    if (score <= 25) return { score, label: "Fraca", color: "#ef4444" };
    if (score <= 50) return { score, label: "Razoável", color: "#f59e0b" };
    if (score <= 75) return { score, label: "Boa", color: "#3b82f6" };
    return { score: Math.min(score, 100), label: "Forte", color: "#10b981" };
  }, [password]);

  // Verificar se senhas coincidem
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const passwordsDontMatch = password && confirmPassword && password !== confirmPassword;

  // Validar token ao carregar a página
  useEffect(() => {
    const tokenParam = searchParams.get("token");
    
    if (!tokenParam) {
      setValidatingToken(false);
      setTokenValid(false);
      setTokenError("Link de recuperação inválido");
      return;
    }

    setToken(tokenParam);

    // Validar token no backend
    const validateToken = async () => {
      try {
        await validateResetToken(tokenParam);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
        setTokenError(
          error.response?.data?.error || 
          "Este link de recuperação é inválido ou já foi utilizado"
        );
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    let toastId;

    try {
      setLoading(true);
      toastId = initializeLoadingToast({
        text: "Redefinindo senha...",
      });

      // Validações
      if (password === "" || confirmPassword === "") {
        throw new Error("Preencha todos os campos");
      }

      if (password.length < 6) {
        throw new Error("A senha deve ter no mínimo 6 caracteres");
      }

      if (password !== confirmPassword) {
        throw new Error("As senhas não coincidem");
      }

      await resetPassword(token, password);

      setSuccess(true);
      
      updateLoadingToast({
        id: toastId,
        text: "Senha redefinida com sucesso!",
        type: "success",
      });

      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate("/login");
      }, 3000);

    } catch (error) {
      updateLoadingToast({
        id: toastId,
        text: error.response?.data?.error || error.message || "Erro ao redefinir senha",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = password.length >= 6 && passwordsMatch && token;

  // Tela de carregamento enquanto valida o token
  if (validatingToken) {
    return (
      <Box
        display="flex"
        width="100vw"
        height="100vh"
        sx={{
          backgroundColor: "#1D1F2D",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            color: "#fff",
          }}
        >
          <CircularProgress sx={{ color: "#CA9900", mb: 2 }} />
          <Typography variant="body1">Validando link de recuperação...</Typography>
        </Box>
      </Box>
    );
  }

  // Tela de erro se token inválido
  if (!tokenValid && !validatingToken) {
    return (
      <Box
        display="flex"
        width="100vw"
        height="100vh"
        sx={{
          backgroundColor: "#1D1F2D",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          display="flex"
          width="90%"
          maxWidth="450px"
          sx={{
            borderRadius: 4,
            backgroundColor: "#ffffff",
            flexDirection: "column",
            alignItems: "center",
            p: 5,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            position: "relative",
            zIndex: 1,
          }}
        >
          <img
            width="55%"
            style={{ marginBottom: 24 }}
            src={CompleteLogo}
            alt="Logo JBE Engenharia"
          />

          {/* Divisor */}
          <Box
            sx={{
              width: "60%",
              height: "1px",
              background: "linear-gradient(to right, transparent, #e5e7eb, transparent)",
              mb: 3,
            }}
          />

          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 32, color: "#fff" }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: "#1e293b",
              fontWeight: 700,
              mb: 1,
              textAlign: "center",
            }}
          >
            Link inválido ou expirado
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              mb: 4,
              textAlign: "center",
              px: 2,
              lineHeight: 1.6,
            }}
          >
            {tokenError}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate("/forgot-password")}
            sx={{
              backgroundColor: "#CA9900",
              color: "#fff",
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              boxShadow: "0 4px 14px rgba(202, 153, 0, 0.4)",
              "&:hover": {
                backgroundColor: "#b38800",
              },
            }}
          >
            Solicitar novo link
          </Button>

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e5e7eb",
              width: "100%",
              textAlign: "center",
            }}
          >
            <NavLink
              to="/login"
              style={{
                textDecoration: "none",
                color: "#64748b",
                fontSize: "0.875rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 500,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              Voltar para login
            </NavLink>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      width="100vw"
      height="100vh"
      sx={{
        backgroundColor: "#1D1F2D",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "600px",
          height: "600px",
          background: success 
            ? "radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(202, 153, 0, 0.15) 0%, transparent 70%)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        display="flex"
        width="90%"
        maxWidth="450px"
        sx={{
          borderRadius: 4,
          backgroundColor: "#ffffff",
          flexDirection: "column",
          alignItems: "center",
          p: 5,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <img
          width="55%"
          style={{ marginBottom: 24 }}
          src={CompleteLogo}
          alt="Logo JBE Engenharia"
        />

        {/* Divisor */}
        <Box
          sx={{
            width: "60%",
            height: "1px",
            background: "linear-gradient(to right, transparent, #e5e7eb, transparent)",
            mb: 3,
          }}
        />

        {/* Ícone de status - só mostra quando sucesso */}
        {success && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: "#fff" }} />
          </Box>
        )}
        
        <Typography
          variant="h5"
          sx={{
            color: "#1e293b",
            fontWeight: 700,
            mb: 0.5,
            textAlign: "center",
          }}
        >
          {success ? "Senha redefinida!" : "Criar nova senha"}
        </Typography>
        
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            mb: 4,
            textAlign: "center",
            px: 2,
            lineHeight: 1.6,
          }}
        >
          {success 
            ? "Sua senha foi alterada com sucesso. Você será redirecionado para o login."
            : "Crie uma nova senha segura para sua conta"
          }
        </Typography>

        <ToastContainer />

        {!success && (
          <form style={{ width: "100%" }} onSubmit={handleSubmit}>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              Nova senha
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              placeholder="Mínimo 6 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#9ca3af" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: "#CA9900",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#CA9900",
                    borderWidth: 2,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.5,
                },
              }}
            />

            {/* Indicador de força da senha */}
            {password && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Força da senha
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ color: passwordStrength.color, fontWeight: 600 }}
                  >
                    {passwordStrength.label}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={passwordStrength.score} 
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#e5e7eb",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: passwordStrength.color,
                      borderRadius: 3,
                    }
                  }}
                />
              </Box>
            )}

            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              Confirmar senha
            </Typography>
            <TextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Digite novamente"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              error={passwordsDontMatch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: "#9ca3af" }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: passwordsMatch ? "#10b981" : passwordsDontMatch ? "#ef4444" : "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: passwordsMatch ? "#10b981" : passwordsDontMatch ? "#ef4444" : "#CA9900",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: passwordsMatch ? "#10b981" : passwordsDontMatch ? "#ef4444" : "#CA9900",
                    borderWidth: 2,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.5,
                },
              }}
            />

            {/* Feedback de confirmação */}
            {confirmPassword && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: "block",
                  mb: 2,
                  color: passwordsMatch ? "#10b981" : "#ef4444",
                  fontWeight: 500
                }}
              >
                {passwordsMatch ? "✓ Senhas coincidem" : "✗ Senhas não coincidem"}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !isFormValid}
              sx={{
                backgroundColor: (!isFormValid || loading) ? "#e5e7eb" : "#CA9900",
                color: (!isFormValid || loading) ? "#9ca3af" : "#fff",
                py: 1.5,
                mt: 2,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                boxShadow: (!isFormValid || loading) ? "none" : "0 4px 14px rgba(202, 153, 0, 0.4)",
                "&:hover": {
                  backgroundColor: (!isFormValid || loading) ? "#e5e7eb" : "#b38800",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Redefinir senha"
              )}
            </Button>
          </form>
        )}

        {success && (
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <CircularProgress size={24} sx={{ color: "#CA9900" }} />
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#64748b" }}>
              Redirecionando para o login...
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "1px solid #e5e7eb",
            width: "100%",
            textAlign: "center",
          }}
        >
          <NavLink
            to="/login"
            style={{
              textDecoration: "none",
              color: "#64748b",
              fontSize: "0.875rem",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 500,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Voltar para login
          </NavLink>
        </Box>
      </Box>
    </Box>
  );
}
